/**
 * @file VariantBulkFill.tsx
 *
 * Sets one column value — price, sale price, or stock — across every sellable variant
 * row at once, so a merchant with a long matrix does not have to type the same figure
 * into each row (the WSB "Set all variants → Apply to all" control). SKU is deliberately
 * absent: SKUs must be unique per row, so there is nothing sensible to fill them with.
 *
 * Presentational and stateless beyond its own inputs: it reports the columns the
 * merchant filled through `onApply` and the owner applies them to the rows. Only the
 * fields actually entered are sent, so leaving a box blank leaves that column untouched;
 * an unusable value refuses the whole apply with a message rather than silently landing
 * the other columns.
 */

import { useState } from 'react';
import { Button, GeneralField } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import type { ShopProductVariant } from '../../../api/shop-types';
import { isInvalidPriceInput } from '../product-form-utils';
import styles from './VariantBulkFill.module.css';

interface VariantBulkFillProps {
  /** Applies the entered columns to every sellable row (the owner decides which). */
  onApply: (patch: Partial<ShopProductVariant>) => void;
  currencySymbol: string;
}

/**
 * Renders the bulk column-fill row.
 *
 * @param props - The apply handler and the shop currency symbol.
 *
 * @returns The bulk-fill controls.
 */
export function VariantBulkFill({ onApply, currencySymbol }: VariantBulkFillProps) {
  const intl = useIntl();
  const [price, setPrice] = useState('');
  const [sale, setSale] = useState('');
  const [stock, setStock] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const withCurrency = (label: string): string =>
    currencySymbol ? `${label} (${currencySymbol})` : label;

  const apply = (): void => {
    const patch: Partial<ShopProductVariant> = {};

    if (price.trim() !== '') {
      if (isInvalidPriceInput(price)) {
        setError(intl.formatMessage(adminMessages.formVariantRowPriceInvalid));
        return;
      }
      patch.regular_price = price.trim();
    }
    if (sale.trim() !== '') {
      if (isInvalidPriceInput(sale)) {
        setError(intl.formatMessage(adminMessages.formVariantRowSaleInvalid));
        return;
      }
      patch.sale_price = sale.trim();
    }
    if (stock.trim() !== '') {
      const parsed = Number(stock.trim());
      if (!Number.isInteger(parsed) || parsed < 0) {
        setError(intl.formatMessage(adminMessages.formVariantBulkStockInvalid));
        return;
      }
      // Filling a stock number tracks it — unlimited rows switch to a counted quantity,
      // matching what the merchant just typed.
      patch.manage_stock = true;
      patch.stock_quantity = parsed;
    }

    setError(undefined);
    if (Object.keys(patch).length === 0) {
      return;
    }
    onApply(patch);
    setPrice('');
    setSale('');
    setStock('');
  };

  return (
    <div className="gv-flex gv-flex-col gv-gap-xs gv-w-full" data-testid="variant-bulk-fill">
      <div className={styles.row}>
        <span className={`${styles.label} gv-text-sm gv-text-muted`}>
          <FormattedMessage {...adminMessages.formVariantBulkFillLabel} />
        </span>
        <div className={styles.field}>
          <GeneralField
            value={price}
            inputMode="decimal"
            autoComplete="off"
            condensed
            data-testid="variant-bulk-price"
            placeholder={withCurrency(
              intl.formatMessage(adminMessages.formVariantColPrice),
            )}
            aria-label={intl.formatMessage(adminMessages.formVariantBulkPriceAria)}
            onChange={(e) => {
              setPrice(e.target.value);
              setError(undefined);
            }}
          />
        </div>
        <div className={styles.field}>
          <GeneralField
            value={sale}
            inputMode="decimal"
            autoComplete="off"
            condensed
            data-testid="variant-bulk-sale-price"
            placeholder={withCurrency(
              intl.formatMessage(adminMessages.formVariantColSalePrice),
            )}
            aria-label={intl.formatMessage(adminMessages.formVariantBulkSalePriceAria)}
            onChange={(e) => {
              setSale(e.target.value);
              setError(undefined);
            }}
          />
        </div>
        <div className={styles.field}>
          <GeneralField
            value={stock}
            type="number"
            min={0}
            autoComplete="off"
            condensed
            data-testid="variant-bulk-stock"
            placeholder={intl.formatMessage(adminMessages.formVariantColStock)}
            aria-label={intl.formatMessage(adminMessages.formVariantBulkStockAria)}
            onChange={(e) => {
              setStock(e.target.value);
              setError(undefined);
            }}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          condensed
          data-testid="variant-bulk-apply"
          onClick={apply}
        >
          <FormattedMessage {...adminMessages.formVariantBulkApply} />
        </Button>
      </div>
      {error && (
        <span
          className="gv-text-sm gv-state-critical"
          role="alert"
          data-testid="variant-bulk-error"
        >
          {error}
        </span>
      )}
    </div>
  );
}
